/**
 * JANIMONY.CHAT - API SERVICE
 *
 * FIX: 401 interceptor now uses AuthService.logout() instead of raw
 * localStorage.removeItem() — previously it was clearing the wrong keys
 * (janimony_business / janimony_token) while AuthService reads
 * (janimony_business_user / janimony_business_token), causing useAuth
 * to still see a valid user after the 401 clear, triggering an infinite
 * re-render loop that caused the dashboard flash/glitch.
 */

import axios from 'axios';
import AuthService from './auth';

const API_URL = 'https://janimony-backend.onrender.com/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
// FIX: Read token using AuthService so it uses the correct key
api.interceptors.request.use((config) => {
  const token = AuthService.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle responses
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      // FIX: Use AuthService.logout() — clears the correct localStorage keys
      // so useAuth sees user=null, stops re-rendering, and ends the loop
      AuthService.logout();
      window.location.href = '/login';
    }
    return Promise.reject(error.response?.data || error.message);
  }
);

const API = {
  // Authentication
  login: async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      return response;
    } catch (error) {
      if (error.message) {
        return { success: false, message: error.message };
      }
      return { success: false, message: 'Invalid email or password' };
    }
  },

  signup: async (data) => {
    try {
      const response = await api.post('/auth/signup', {
        businessName: data.businessName,
        email: data.email,
        password: data.password,
        plan: data.plan || 'free'
      });
      return response;
    } catch (error) {
      if (error.message) {
        if (
          error.message.toLowerCase().includes('already exists') ||
          error.message.toLowerCase().includes('duplicate') ||
          error.message.toLowerCase().includes('taken')
        ) {
          return {
            success: false,
            message: 'This email is already registered. Please login or use a different email.'
          };
        }
        return { success: false, message: error.message };
      }
      return { success: false, message: 'Unable to create account. Please try again.' };
    }
  },

  // Dashboard
  getStats: async () => {
    try {
      return await api.get('/business/stats');
    } catch (error) {
      return {
        success: true,
        stats: {
          messagesUsed: 0,
          messageLimit: 50,
          activeChats: 0,
          totalChats: 0,
          totalAgents: 0
        }
      };
    }
  },

  // Chats
  getActiveChats: async (businessId) => {
    try {
      return await api.get(`/chat/active/${businessId}`);
    } catch (error) {
      return { success: true, chats: [] };
    }
  },

  getChatMessages: async (businessId, sessionId) => {
    try {
      return await api.get(`/chat/messages/${businessId}/${sessionId}`);
    } catch (error) {
      return { success: true, messages: [] };
    }
  },

  sendMessage: async (sessionId, message) => {
    try {
      return await api.post('/chat/agent-reply', { sessionId, message });
    } catch (error) {
      return { success: false, message: 'Failed to send message' };
    }
  },

  // Agents
  getAgents: async (businessId) => {
    try {
      return await api.get(`/agent/${businessId}`);
    } catch (error) {
      return { success: true, agents: [] };
    }
  },

  addAgent: async (agentData) => {
    try {
      return await api.post('/agent', agentData);
    } catch (error) {
      if (error.message?.includes('already exists')) {
        return { success: false, message: 'Agent with this email already exists' };
      }
      return { success: false, message: 'Failed to add agent' };
    }
  },

  deleteAgent: async (agentId) => {
    try {
      return await api.delete(`/agent/${agentId}`);
    } catch (error) {
      return { success: false, message: 'Failed to delete agent' };
    }
  },

  // Settings
  getSettings: async () => {
    try {
      return await api.get('/business/settings');
    } catch (error) {
      return {
        success: true,
        settings: {
          companyName: 'Demo Business',
          welcomeMessage: 'Hi! How can we help?',
          themePrimary: '#000000',
          themeSecondary: '#FFFFFF'
        }
      };
    }
  },

  updateSettings: async (settings) => {
    try {
      return await api.put('/business/settings', settings);
    } catch (error) {
      return { success: false, message: 'Failed to update settings' };
    }
  },

  // Analytics
  getAnalytics: async (period = '7d') => {
    try {
      return await api.get(`/business/analytics?period=${period}`);
    } catch (error) {
      return {
        success: true,
        analytics: {
          chatsOverTime: [],
          avgResponseTime: 45,
          satisfaction: 4.5
        }
      };
    }
  }
};

export default API;